import axios, { AxiosInstance } from 'axios';
import {
  AccountHolder,
  Payment,
  PaymentSummary,
  TrustAccount,
  CreateTrustAccountRequest,
  UpdateTrustAccountRequest,
  CreatePaymentRequest,
  CreateAccountHolderRequest,
  CreateCreditNoteRequest,
  UpdateCreditNoteRequest,
  AuthResponse,
  RegisterRequest,
  LoginRequest,
  UserDto,
  DashboardSettingsDto,
  UpdateDashboardSettingsRequest,
  FarmWorkType,
  CreateFarmWorkTypeRequest,
  UpdateFarmWorkTypeRequest,
  FarmWork,
  CreateFarmWorkRequest,
  UpdateFarmWorkRequest,
  FarmWorkContribution,
  CreateFarmWorkContributionRequest,
  UpdateFarmWorkContributionRequest,
  EquipmentHiring,
  CreateEquipmentHiringRequest,
  UpdateEquipmentHiringRequest,
  EquipmentHiringContribution,
  CreateEquipmentHiringContributionRequest,
  UpdateEquipmentHiringContributionRequest,
  EquipmentHiringIncomeRecipient,
  CreateEquipmentHiringIncomeRecipientRequest,
  UpdateEquipmentHiringIncomeRecipientRequest,
  TrustStatementPreviewResponse,
  SaveTrustStatementImportRequest,
} from '../types/index';

const configuredApiUrl = process.env.REACT_APP_API_URL?.trim();
const isProductionBuild = process.env.NODE_ENV === 'production';

const API_BASE_URL = configuredApiUrl || (isProductionBuild ? '/api' : 'http://localhost:5000/api');

if (isProductionBuild && !configuredApiUrl) {
  console.error(
    '[API] REACT_APP_API_URL is not set for this production build. Falling back to /api. Set REACT_APP_API_URL (or Amplify branch vars) to your deployed backend URL.'
  );
}

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshInFlightPromise: Promise<string | null> | null = null;

const clearAuthAndRedirectToLogin = () => {
  localStorage.removeItem('authToken');
  localStorage.removeItem('user');
  localStorage.removeItem('authLastActivityAt');
  localStorage.removeItem('authSessionDate');

  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

const logNetworkTroubleshootingHints = () => {
  const browserOrigin = window.location.origin;
  const baseUrl = API_BASE_URL;
  const isExecuteApiUrl = /\.execute-api\.[^/]+\.amazonaws\.com/i.test(baseUrl);
  const appearsToIncludeApiPrefix = /\/api\/?$/i.test(baseUrl);
  const appearsToIncludeStage = /\.amazonaws\.com\/[A-Za-z0-9_-]+/i.test(baseUrl);

  console.error(
    `[API] Network/CORS diagnostics: browserOrigin=${browserOrigin}, apiBaseUrl=${baseUrl}, configuredApiUrl=${configuredApiUrl || '(empty)'}`
  );

  if (isExecuteApiUrl && (!appearsToIncludeStage || !appearsToIncludeApiPrefix)) {
    console.error(
      '[API] REACT_APP_API_URL may be missing API Gateway stage or /api suffix. Expected format: https://<api-id>.execute-api.<region>.amazonaws.com/<stage>/api'
    );
  }

  if (isProductionBuild && !configuredApiUrl) {
    console.error(
      '[API] Production build is using fallback /api because REACT_APP_API_URL is not set. Configure REACT_APP_API_URL (or Amplify branch variables) in hosted environments.'
    );
  }
};

const ACCOUNT_HOLDER_CACHE_TTL_MS = 2000;
const accountHolderInFlightRequests = new Map<string, Promise<AccountHolder[]>>();
const accountHolderResponseCache = new Map<string, { data: AccountHolder[]; fetchedAt: number }>();

const getAccountHolderCacheKey = (includeSystem: boolean) => `includeSystem:${includeSystem ? '1' : '0'}`;

const clearAccountHolderCache = () => {
  accountHolderInFlightRequests.clear();
  accountHolderResponseCache.clear();
};

const FARM_WORK_TYPES_CACHE_TTL_MS = 2000;
const farmWorkTypesInFlightRequest = { current: null as Promise<FarmWorkType[]> | null };
const farmWorkTypesResponseCache = { current: null as { data: FarmWorkType[]; fetchedAt: number } | null };

const clearFarmWorkTypesCache = () => {
  farmWorkTypesInFlightRequest.current = null;
  farmWorkTypesResponseCache.current = null;
};

const PAYMENT_GET_ALL_CACHE_TTL_MS = 1500;
const paymentGetAllInFlightRequests = new Map<string, Promise<Payment[]>>();
const paymentGetAllResponseCache = new Map<string, { data: Payment[]; fetchedAt: number }>();

const PAYMENT_SUMMARIES_CACHE_TTL_MS = 1500;
const paymentSummariesInFlightRequests = new Map<string, Promise<PaymentSummary[]>>();
const paymentSummariesResponseCache = new Map<string, { data: PaymentSummary[]; fetchedAt: number }>();

const getPaymentGetAllCacheKey = (accountHolderId?: number, startDate?: string, endDate?: string) => {
  const accountHolderPart = accountHolderId ?? 'all';
  const startPart = startDate ?? 'none';
  const endPart = endDate ?? 'none';
  return `${accountHolderPart}|${startPart}|${endPart}`;
};

const clearPaymentGetAllCache = () => {
  paymentGetAllInFlightRequests.clear();
  paymentGetAllResponseCache.clear();
};

const getPaymentSummariesCacheKey = (startDate?: string, endDate?: string) => {
  const startPart = startDate ?? 'none';
  const endPart = endDate ?? 'none';
  return `${startPart}|${endPart}`;
};

const clearPaymentSummariesCache = () => {
  paymentSummariesInFlightRequests.clear();
  paymentSummariesResponseCache.clear();
};

const clearPaymentQueryCaches = () => {
  clearPaymentGetAllCache();
  clearPaymentSummariesCache();
};

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle 401 responses
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!error.response && error.code === 'ERR_NETWORK') {
      console.error(
        `[API] Network error reaching backend at ${API_BASE_URL}. Check deployed API availability, DNS/SSL, and REACT_APP_API_URL.`
      );
      logNetworkTroubleshootingHints();
    }

    if (error.response?.status === 401) {
      const originalRequest = error.config as any;
      const requestUrl = String(originalRequest?.url || '');
      const isRefreshRequest = requestUrl.includes('/auth/refresh');
      const isLoginOrRegisterRequest = requestUrl.includes('/auth/login') || requestUrl.includes('/auth/register');

      if (isLoginOrRegisterRequest) {
        return Promise.reject(error);
      }

      if (isRefreshRequest) {
        clearAuthAndRedirectToLogin();
        return Promise.reject(error);
      }

      if (!originalRequest || originalRequest._retry) {
        clearAuthAndRedirectToLogin();
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      try {
        if (!refreshInFlightPromise) {
          refreshInFlightPromise = authService.refresh()
            .then((response) => {
              if (response.success && response.token) {
                localStorage.setItem('authToken', response.token);
                if (response.user) {
                  localStorage.setItem('user', JSON.stringify(response.user));
                }
                return response.token;
              }
              clearAuthAndRedirectToLogin();
              return null;
            })
            .catch(() => {
              clearAuthAndRedirectToLogin();
              return null;
            })
            .finally(() => {
              refreshInFlightPromise = null;
            });
        }

        const refreshedToken = await refreshInFlightPromise;
        if (refreshedToken) {
          originalRequest.headers = originalRequest.headers || {};
          originalRequest.headers.Authorization = `Bearer ${refreshedToken}`;
          return api(originalRequest);
        }
      } catch {
        clearAuthAndRedirectToLogin();
      }
    }

    return Promise.reject(error);
  }
);

// Authentication Services
export const authService = {
  register: async (request: RegisterRequest): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/register', request);
    return response.data;
  },

  login: async (request: LoginRequest): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/login', request);
    return response.data;
  },

  refresh: async (): Promise<AuthResponse> => {
    const response = await api.post<AuthResponse>('/auth/refresh');
    return response.data;
  },

  getProfile: async (): Promise<UserDto> => {
    const response = await api.get<UserDto>('/auth/profile');
    return response.data;
  },

  getAllUsers: async (): Promise<UserDto[]> => {
    const response = await api.get<UserDto[]>('/auth/users');
    return response.data;
  },

  createUser: async (request: any): Promise<any> => {
    const response = await api.post('/auth/users', request);
    return response.data;
  },

  updateUser: async (id: number, request: any): Promise<any> => {
    const response = await api.put(`/auth/users/${id}`, request);
    return response.data;
  },

  deleteUser: async (id: number): Promise<any> => {
    const response = await api.delete(`/auth/users/${id}`);
    return response.data;
  },
};

// Account Holder Services
export const accountHolderService = {
  getAll: async (includeSystem = false): Promise<AccountHolder[]> => {
    const requestKey = getAccountHolderCacheKey(includeSystem);
    const now = Date.now();
    const cached = accountHolderResponseCache.get(requestKey);

    if (cached && (now - cached.fetchedAt) < ACCOUNT_HOLDER_CACHE_TTL_MS) {
      return cached.data;
    }

    const inFlight = accountHolderInFlightRequests.get(requestKey);
    if (inFlight) {
      return inFlight;
    }

    const request = api.get<AccountHolder[]>('/accountholders', {
      params: { includeSystem },
    })
      .then((response) => {
        accountHolderResponseCache.set(requestKey, { data: response.data, fetchedAt: Date.now() });
        return response.data;
      })
      .finally(() => {
        accountHolderInFlightRequests.delete(requestKey);
      });

    accountHolderInFlightRequests.set(requestKey, request);
    return request;
  },

  getById: async (id: number): Promise<AccountHolder> => {
    const response = await api.get<AccountHolder>(`/accountholders/${id}`);
    return response.data;
  },

  create: async (data: CreateAccountHolderRequest): Promise<AccountHolder> => {
    const response = await api.post<AccountHolder>('/accountholders', data);
    clearAccountHolderCache();
    return response.data;
  },

  update: async (id: number, data: CreateAccountHolderRequest): Promise<AccountHolder> => {
    const response = await api.put<AccountHolder>(`/accountholders/${id}`, data);
    clearAccountHolderCache();
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/accountholders/${id}`);
    clearAccountHolderCache();
  },

  toggleStatus: async (id: number): Promise<AccountHolder> => {
    const response = await api.put<AccountHolder>(`/accountholders/${id}/toggle-status`);
    clearAccountHolderCache();
    return response.data;
  },

  sendCustomEmail: async (payload: { accountHolderIds?: number[]; subject: string; bodyHtml: string; sendToAllActive?: boolean }): Promise<any> => {
    const response = await api.post('/accountholders/send-custom-email', payload);
    return response.data;
  },

  addCreditNote: async (accountHolderId: number, data: CreateCreditNoteRequest): Promise<AccountHolder> => {
    const response = await api.post<AccountHolder>(`/accountholders/${accountHolderId}/credit-notes`, data);
    clearAccountHolderCache();
    return response.data;
  },

  updateCreditNote: async (accountHolderId: number, creditNoteId: string, data: UpdateCreditNoteRequest): Promise<AccountHolder> => {
    const response = await api.put<AccountHolder>(`/accountholders/${accountHolderId}/credit-notes/${creditNoteId}`, data);
    clearAccountHolderCache();
    return response.data;
  },

  deleteCreditNote: async (accountHolderId: number, creditNoteId: string): Promise<AccountHolder> => {
    const response = await api.delete<AccountHolder>(`/accountholders/${accountHolderId}/credit-notes/${creditNoteId}`);
    clearAccountHolderCache();
    return response.data;
  },
};

// Payment Services
export const paymentService = {
  getAll: async (
    accountHolderId?: number,
    startDate?: string,
    endDate?: string
  ): Promise<Payment[]> => {
    const requestKey = getPaymentGetAllCacheKey(accountHolderId, startDate, endDate);
    const now = Date.now();
    const cached = paymentGetAllResponseCache.get(requestKey);

    if (cached && (now - cached.fetchedAt) < PAYMENT_GET_ALL_CACHE_TTL_MS) {
      return cached.data;
    }

    const inFlight = paymentGetAllInFlightRequests.get(requestKey);
    if (inFlight) {
      return inFlight;
    }

    const request = api.get<Payment[]>('/payments', {
      params: {
        accountHolderId,
        startDate,
        endDate,
      },
    })
      .then((response) => {
        paymentGetAllResponseCache.set(requestKey, { data: response.data, fetchedAt: Date.now() });
        return response.data;
      })
      .finally(() => {
        paymentGetAllInFlightRequests.delete(requestKey);
      });

    paymentGetAllInFlightRequests.set(requestKey, request);
    return request;
  },

  create: async (data: CreatePaymentRequest): Promise<Payment> => {
    const response = await api.post<Payment>('/payments', data);
    clearPaymentQueryCaches();
    return response.data;
  },

  update: async (id: number, data: Partial<CreatePaymentRequest>): Promise<Payment> => {
    const response = await api.put<Payment>(`/payments/${id}`, data);
    clearPaymentQueryCaches();
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/payments/${id}`);
    clearPaymentQueryCaches();
  },

  getSummaries: async (startDate?: string, endDate?: string): Promise<PaymentSummary[]> => {
    const requestKey = getPaymentSummariesCacheKey(startDate, endDate);
    const now = Date.now();
    const cached = paymentSummariesResponseCache.get(requestKey);

    if (cached && (now - cached.fetchedAt) < PAYMENT_SUMMARIES_CACHE_TTL_MS) {
      return cached.data;
    }

    const inFlight = paymentSummariesInFlightRequests.get(requestKey);
    if (inFlight) {
      return inFlight;
    }

    const request = api.get<PaymentSummary[]>('/payments/summaries/all', {
      params: {
        startDate,
        endDate,
      },
    })
      .then((response) => {
        paymentSummariesResponseCache.set(requestKey, { data: response.data, fetchedAt: Date.now() });
        return response.data;
      })
      .finally(() => {
        paymentSummariesInFlightRequests.delete(requestKey);
      });

    paymentSummariesInFlightRequests.set(requestKey, request);
    return request;
  },

  sendStatement: async (accountHolderId: number, startDate?: string, endDate?: string, trustAccountId?: number): Promise<any> => {
    const response = await api.post(
      `/payments/${accountHolderId}/send-statement`,
      null,
      {
        params: {
          startDate,
          endDate,
          trustAccountId,
        },
      }
    );
    return response.data;
  },
  getTrustTransactions: async (trustAccountId: number, startDate?: string, endDate?: string): Promise<Payment[]> => {
    const response = await api.get<Payment[]>(`/payments/trust/${trustAccountId}/transactions`, {
      params: { startDate, endDate },
    });
    return response.data;
  },

  previewTrustStatementImport: async (trustAccountId: number, file: File): Promise<TrustStatementPreviewResponse> => {
    const form = new FormData();
    form.append('file', file);
    const response = await api.post<TrustStatementPreviewResponse>(`/payments/trust/${trustAccountId}/import/preview`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  saveTrustStatementImport: async (trustAccountId: number, request: SaveTrustStatementImportRequest): Promise<any> => {
    const response = await api.post(`/payments/trust/${trustAccountId}/import/save`, request);
    clearPaymentQueryCaches();
    return response.data;
  },
};

export const trustAccountService = {
  getAll: async (): Promise<TrustAccount[]> => {
    const response = await api.get<TrustAccount[]>('/trustaccounts');
    return response.data;
  },

  create: async (payload: CreateTrustAccountRequest): Promise<TrustAccount> => {
    const response = await api.post<TrustAccount>('/trustaccounts', payload);
    return response.data;
  },

  update: async (id: number, payload: UpdateTrustAccountRequest): Promise<TrustAccount> => {
    const response = await api.put<TrustAccount>(`/trustaccounts/${id}`, payload);
    return response.data;
  },
};

export const dashboardSettingsService = {
  get: async (): Promise<DashboardSettingsDto> => {
    const response = await api.get<DashboardSettingsDto>('/dashboardsettings');
    return response.data;
  },

  update: async (request: UpdateDashboardSettingsRequest): Promise<DashboardSettingsDto> => {
    const response = await api.put<DashboardSettingsDto>('/dashboardsettings', request);
    return response.data;
  },
};

const farmWorkMonthInFlightRequests = new Map<string, Promise<FarmWork[]>>();
const farmWorkMonthResponseCache = new Map<string, { data: FarmWork[]; fetchedAt: number }>();
const FARM_WORK_MONTH_CACHE_TTL_MS = 2000;

const clearFarmWorkMonthCache = () => {
  farmWorkMonthInFlightRequests.clear();
  farmWorkMonthResponseCache.clear();
};

// Farm Work Services
export const farmWorkService = {
  // Farm Work Type methods
  getAllTypes: async (): Promise<FarmWorkType[]> => {
    const now = Date.now();
    const cached = farmWorkTypesResponseCache.current;

    if (cached && (now - cached.fetchedAt) < FARM_WORK_TYPES_CACHE_TTL_MS) {
      return cached.data;
    }

    if (farmWorkTypesInFlightRequest.current) {
      return farmWorkTypesInFlightRequest.current;
    }

    farmWorkTypesInFlightRequest.current = api.get<FarmWorkType[]>('/farmwork/types')
      .then((response) => {
        farmWorkTypesResponseCache.current = { data: response.data, fetchedAt: Date.now() };
        return response.data;
      })
      .finally(() => {
        farmWorkTypesInFlightRequest.current = null;
      });

    return farmWorkTypesInFlightRequest.current;
  },

  createType: async (data: CreateFarmWorkTypeRequest): Promise<FarmWorkType> => {
    const response = await api.post<FarmWorkType>('/farmwork/types', data);
    clearFarmWorkTypesCache();
    return response.data;
  },

  updateType: async (id: number, data: UpdateFarmWorkTypeRequest): Promise<FarmWorkType> => {
    const response = await api.put<FarmWorkType>(`/farmwork/types/${id}`, data);
    clearFarmWorkTypesCache();
    return response.data;
  },

  deleteType: async (id: number): Promise<void> => {
    await api.delete(`/farmwork/types/${id}`);
    clearFarmWorkTypesCache();
  },

  // Farm Work methods
  getAll: async (): Promise<FarmWork[]> => {
    const response = await api.get<FarmWork[]>('/farmwork');
    return response.data;
  },

  getByMonthYear: async (month: number, year: number): Promise<FarmWork[]> => {
    const requestKey = `${year}-${month}`;
    const now = Date.now();
    const cached = farmWorkMonthResponseCache.get(requestKey);

    if (cached && (now - cached.fetchedAt) < FARM_WORK_MONTH_CACHE_TTL_MS) {
      return cached.data;
    }

    const inFlight = farmWorkMonthInFlightRequests.get(requestKey);
    if (inFlight) {
      return inFlight;
    }

    const request = api.get<FarmWork[]>(`/farmwork/month/${month}/year/${year}`)
      .then((response) => {
        farmWorkMonthResponseCache.set(requestKey, { data: response.data, fetchedAt: Date.now() });
        return response.data;
      })
      .finally(() => {
        farmWorkMonthInFlightRequests.delete(requestKey);
      });

    farmWorkMonthInFlightRequests.set(requestKey, request);
    return request;
  },

  create: async (data: CreateFarmWorkRequest): Promise<FarmWork> => {
    const response = await api.post<FarmWork>('/farmwork', data);
    clearFarmWorkMonthCache();
    return response.data;
  },

  update: async (id: number, data: UpdateFarmWorkRequest): Promise<FarmWork> => {
    const response = await api.put<FarmWork>(`/farmwork/${id}`, data);
    clearFarmWorkMonthCache();
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/farmwork/${id}`);
    clearFarmWorkMonthCache();
  },

  // Contribution methods
  addContribution: async (data: CreateFarmWorkContributionRequest): Promise<FarmWorkContribution> => {
    const response = await api.post<FarmWorkContribution>('/farmwork/contributions', data);
    clearFarmWorkMonthCache();
    return response.data;
  },

  updateContribution: async (id: number, data: UpdateFarmWorkContributionRequest): Promise<FarmWorkContribution> => {
    const response = await api.put<FarmWorkContribution>(`/farmwork/contributions/${id}`, data);
    clearFarmWorkMonthCache();
    return response.data;
  },

  removeContribution: async (id: number): Promise<void> => {
    await api.delete(`/farmwork/contributions/${id}`);
    clearFarmWorkMonthCache();
  },
};

export const equipmentHiringService = {
  getByMonthYear: async (month: number, year: number): Promise<EquipmentHiring[]> => {
    const response = await api.get<EquipmentHiring[]>(`/equipmenthiring/byMonth/${month}/${year}`);
    return response.data;
  },

  create: async (data: CreateEquipmentHiringRequest): Promise<EquipmentHiring> => {
    const response = await api.post<EquipmentHiring>('/equipmenthiring', data);
    return response.data;
  },

  update: async (id: number, data: UpdateEquipmentHiringRequest): Promise<EquipmentHiring> => {
    const response = await api.put<EquipmentHiring>(`/equipmenthiring/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/equipmenthiring/${id}`);
  },

  addContribution: async (data: CreateEquipmentHiringContributionRequest): Promise<EquipmentHiringContribution> => {
    const response = await api.post<EquipmentHiringContribution>('/equipmenthiring/contributions', data);
    return response.data;
  },

  updateContribution: async (id: number, data: UpdateEquipmentHiringContributionRequest): Promise<EquipmentHiringContribution> => {
    const response = await api.put<EquipmentHiringContribution>(`/equipmenthiring/contributions/${id}`, data);
    return response.data;
  },

  removeContribution: async (id: number): Promise<void> => {
    await api.delete(`/equipmenthiring/contributions/${id}`);
  },

  addIncomeRecipient: async (data: CreateEquipmentHiringIncomeRecipientRequest): Promise<EquipmentHiringIncomeRecipient> => {
    const response = await api.post<EquipmentHiringIncomeRecipient>('/equipmenthiring/income-recipients', data);
    return response.data;
  },

  updateIncomeRecipient: async (id: number, data: UpdateEquipmentHiringIncomeRecipientRequest): Promise<EquipmentHiringIncomeRecipient> => {
    const response = await api.put<EquipmentHiringIncomeRecipient>(`/equipmenthiring/income-recipients/${id}`, data);
    return response.data;
  },

  removeIncomeRecipient: async (id: number): Promise<void> => {
    await api.delete(`/equipmenthiring/income-recipients/${id}`);
  },
};

export default api;
